import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import MyAccountSideBar from './MyAccountSideBar';
import { CommonService } from '../../services/common';
import { decryptData, errorHandling, UpdatedDecimalDigits } from '../../helpers/common';
import pathObj from '../../services/Config';
import debounce from 'lodash.debounce';
import getSymbolFromCurrency from 'currency-symbol-map';
import toast from 'react-hot-toast';
import BraintreePaymentModal from './BraintreePaymentModal';

const AccountBalance = () => {
  const userInfo = window.localStorage.getItem('userData');
  const userD = decryptData(userInfo);
  const compData = JSON.parse(userD);
  const customerId = compData?.customer_id;
  const currencyName = compData?.Currency;
  const currencySymbol = getSymbolFromCurrency(currencyName);
  const [loading, setLoading] = useState(false);
  const [accountBalance, setAccountBalance] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const selectedInvoiceId = selectedInvoices.filter(item => item).join(', ');
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [selectedInvoicesAmount, setSelectedInvoicesAmount] = useState(0);
  const [errorText, setErrorText] = useState('Balance not found.');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);
  const [specificAmount, setSpecificAmount] = useState(0);
  const [balanceDue, setBalanceDue] = useState(0);
  const [futureRemit, setFutureRemit] = useState(0);
  const [overdue0to30, setOverdue0to30] = useState(0);
  const [overdue31to60, setOverdue31to60] = useState(0);
  const [overdue61to90, setOverdue61to90] = useState(0);
  const [overdue91to120, setOverdue91to120] = useState(0);
  const [overdue121plus, setOverdue121plus] = useState(0);
  const [overdue_tillnow, setOverdueTillNow] = useState(0);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    if (specificAmount <= 0 && selectedInvoicesAmount <= 0) {
      toast.error('Please enter a positive amount to proceed to payment');
    } else {
      openModal();
    }
  };
 
  const fetchAccountBalance = async () => {
    setLoading(true);
    const url = `${pathObj.baseURL}${pathObj.getUserAccountBalance}`;
    const payload = {
      customer_id: customerId,
    };
    try {
      const response = await CommonService.getAllWithBodyWithToken(url, payload);
      if (response.success === true) {
        setAccountBalance(response?.data);

        // Set summary values from sub_data[0]
        if (response.sub_data && response.sub_data[0]) {
          const sub = response.sub_data[0];
          setBalanceDue(sub.balance_due);
          setFutureRemit(sub.future_remit);
          setOverdue0to30(sub.overdue_0_30);
          setOverdue31to60(sub.overdue_31_60);
          setOverdue61to90(sub.overdue_61_90);
          setOverdue91to120(sub.overdue_91_120);
          setOverdue121plus(sub.overdue_121_plus);
          setOverdueTillNow(sub.overdue_tillnow);
        }
      } else {
        setErrorText(response?.message);
      }
    } catch (error) {
      setErrorText(error?.response?.message);
      errorHandling(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (item) => {
    
    const isSelected = selectedInvoices.includes(item.DocEntry);
    let updatedSelectedInvoices;

    // Toggle selection of the checkbox
    if (isSelected) {
      updatedSelectedInvoices = selectedInvoices.filter(id => id !== item.DocEntry);
    } else {
      updatedSelectedInvoices = [...selectedInvoices, item.DocEntry];
    }

    setSelectedInvoices(updatedSelectedInvoices);

    // Recalculate the total debit and credit values based on updated selections
    let newTotalDebit = 0;
    let newTotalCredit = 0;
    accountBalance.forEach(inv => {
      if (updatedSelectedInvoices.includes(inv.DocEntry)) {
        const debitValue = parseFloat(inv.debit) || 0;  // Convert debit string to number
        const creditValue = parseFloat(inv.credit) || 0;  // Convert credit string to number
        newTotalDebit += debitValue;
        newTotalCredit += creditValue;
      }
    });

    setTotalDebit(newTotalDebit);
    setTotalCredit(newTotalCredit);

    // Calculate the amount and show the negative value if debit is smaller than credit
    const calculatedAmount = newTotalDebit - newTotalCredit;

    setSelectedInvoicesAmount(calculatedAmount); // Now this can show a negative value
  };

  const debouncedFetchAccountBalance = debounce(fetchAccountBalance, 300);

  useEffect(() => {
    debouncedFetchAccountBalance();
  }, [customerId]);

  const downloadPDF = async (customerId) => {
    try {
      // console.log('Downloading PDF for order ID:', orderId);
      const response = await fetch(`${pathObj.baseURL}/customers/${customerId}/statement-pdf`, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf',
        },
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'customer-statements.pdf';
      link.click();
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  }

  return (
    <>
      {loading && (<div className='loaderOuter'><div className="loader"></div></div>)}
      <div className="cetogry_listing">
        <div className="container">
          <div className="clinner d-flex flex-wrap align-items-start">
            <MyAccountSideBar />

            <div className="common_right_block">
              <div className="contact_details">
                <h1>Account Balance / Pay Bill</h1>

                <div className="aged_details">
                  <h4>Account Balance</h4>
                  <p>Aged Balance Details of your Account</p>
                  <ul>
                    <li>
                      Balance Due <br />
                      {currencySymbol}{UpdatedDecimalDigits(balanceDue ?? 0)}
                    </li>
                    <li>
                      Future Remit <br />
                      {currencySymbol}{UpdatedDecimalDigits(futureRemit ?? 0)}
                    </li>
                    <li>
                      0-30 Over Due <br />
                      {currencySymbol}{UpdatedDecimalDigits(overdue0to30 ?? 0)}
                    </li>
                    <li style={{ color: '#FEB41D' }}>
                      31-60 Over Due <br />
                      {currencySymbol}{UpdatedDecimalDigits(overdue31to60 ?? 0)}
                    </li>
                    <li style={{ color: '#FEB41D' }}>
                      61-90 Over Due <br />
                      {currencySymbol}{UpdatedDecimalDigits(overdue61to90 ?? 0)}
                    </li>
                    <li style={{ color: '#FEB41D' }}>
                      91-120 Over Due <br />
                      {currencySymbol}{UpdatedDecimalDigits(overdue91to120 ?? 0)}
                    </li>
                    <li style={{ color: '#FEB41D' }}>
                      121+ Over Due <br />
                      {currencySymbol}{UpdatedDecimalDigits(overdue121plus ?? 0)}
                    </li>
                  </ul>

                  <div className="oda">Overdue Amount - {currencySymbol}{UpdatedDecimalDigits(overdue_tillnow ?? 0)}</div>

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-check custom_check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="specificAmount"
                        {...register('specificAmount')}
                        disabled={selectedInvoices.length > 0}
                        onChange={() => setSpecificAmount(0)}
                      />
                      <label className="form-check-label" htmlFor="specificAmount">
                        Pay a Specific Amount : {currencySymbol}
                      </label>
                      {/* <input
                        type="text"
                        className="form-control"
                        value={specificAmount || ''}
                        onChange={(e) => setSpecificAmount(parseFloat(e.target.value) || 0)}
                      /> */}
                      <input
                        type="text"
                        className="form-control"
                        value={specificAmount}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow empty or valid decimal pattern
                          if (val === '' || /^[0-9]*\.?[0-9]*$/.test(val)) {
                            setSpecificAmount(val);
                          }
                        }}
                        onBlur={() => {
                          // Convert to float on blur if needed
                          if (specificAmount !== '') {
                            setSpecificAmount(parseFloat(specificAmount).toFixed(2));
                          }
                        }}
                      />

                    </div>

                    <div className="form-check custom_check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="selectedInvoices"
                        {...register('selectedInvoices')}
                        checked={selectedInvoices.length > 0}
                        onChange={() => {
                          if (document.getElementById('specificAmount').checked) {
                            document.getElementById('specificAmount').checked = false;
                            setSpecificAmount(0);
                          }
                        }}
                      />
                      <label className="form-check-label" htmlFor="selectedInvoices">
                        Pay Selected invoices : {currencySymbol}
                      </label>
                      <input
                        type="text"
                        value={selectedInvoicesAmount.toFixed(2)}
                        className="form-control"
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!isNaN(value) && value >= 0) {
                            setSelectedInvoicesAmount(value);
                          }
                        }}
                        {...register('selectedInvoicesAmount', {
                          required: 'Amount is required',
                          pattern: {
                            value: /^[0-9]+(\.[0-9]{1,2})?$/,
                            message: 'Invalid amount format'
                          }
                        })}
                      />
                      {errors.selectedInvoicesAmount && <div className="invalid-feedback">{errors.selectedInvoicesAmount.message}</div>}

                      <button
                        type="button"
                        className="button5"
                        style={{ marginLeft: '10px' }}
                        onClick={onSubmit}
                      >
                        Pay Now
                      </button>

                      <BraintreePaymentModal
                        show={modalIsOpen}
                        hide={closeModal}
                        selectedInvoices={selectedInvoiceId}
                        selectedInvoicesAmount={specificAmount > 0 ? specificAmount : selectedInvoicesAmount}
                        customerId={customerId}
                        reset={reset}
                        setSelectedInvoices={setSelectedInvoices}
                        setTotalDebit={setTotalDebit}
                        setSelectedInvoicesAmount={setSelectedInvoicesAmount}
                        setTotalCredit={setTotalCredit}
                      />
                    </div>
                  </form>

                  <div className="pleasenote">Please Note: Payment discount is not allowed when paying by card</div>
                </div>

                {/* Selected Invoices Section */}
                        <div className="selected-invoices-section" style={{ width: '100%', margin: '30px 0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '40px' }}>
                          <h3 style={{ textAlign: 'left', margin: 0 }}>Outstanding Invoices/Credits</h3>
                          <button className="btn btn-primary btn-sm button5" onClick={() => downloadPDF(customerId)} title="Download Customer Statement">
                            Download <i className="fas fa-download"></i>
                          </button>
                          </div>
                          <div className="common_table table-responsive" style={{ marginTop: 20 }}>
                          <table>
                            <thead>
                            <tr>
                              <th>Inv.Date</th>
                              <th>Invoice No.</th>
                              <th>Details</th>
                              <th>Ref.No.</th>
                              <th>Due Date</th>
                              <th>Debit</th>
                              <th>Credit</th>
                              <th>Balance</th>
                              <th style={{ textAlign: 'center' }}>Select</th>
                            </tr>
                            </thead>
                            <tbody>
                            {accountBalance.map((item) => {
                              // Helper to format date as d/m/Y
                              const formatDate = (dateStr) => {
                                  if (!dateStr) return 'N/A';
                                const date = new Date(dateStr);
                                  if (isNaN(date)) return 'N/A';
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${day}/${month}/${year}`;
                              };
                              return (
                              <tr key={item.id}>
                                <td>{formatDate(item.invoice_date)}</td>
                                <td>{item.invoice_number}</td>
                                <td>{item.details}</td>
                                <td>{item.refrence_number || 'N/A'}</td>
                                <td>{formatDate(item.due_date)}</td>
                                <td>{currencySymbol}{item?.debit ? UpdatedDecimalDigits(item?.debit) : 'N/A'}</td>
                                <td>{currencySymbol}{item?.credit ? UpdatedDecimalDigits(item?.credit) : 'N/A'}</td>
                                <td>{UpdatedDecimalDigits(item?.balance)}</td>
                                <td style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  onChange={() => handleCheckboxChange(item)}
                                  checked={selectedInvoices.includes(item.DocEntry)}
                                />
                                </td>
                              </tr>
                              );
                            })}
                            </tbody>
                          </table>
                          </div>
                        </div>
                        {/* End Selected Invoices Section */}

              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default AccountBalance;
